import { useCallback, useEffect, useRef, useState } from 'react'
import type { Tab } from './components/navTabs'
import {
  type DragDirection,
  resolveDragAxis,
  resolveDragCommit,
} from './dragGesture'

// Re-exported so existing importers of these primitives (and the drag-nav unit
// tests) keep working now that the pure logic lives in `dragGesture.ts`.
export {
  AXIS_LOCK_DISTANCE,
  DRAG_MAX_OFF_AXIS_RATIO,
  DRAG_MIN_DISTANCE,
  DRAG_MIN_VELOCITY,
  resolveDragAxis,
  resolveDragCommit,
} from './dragGesture'
export type { DragDirection } from './dragGesture'

type Phase = 'idle' | 'dragging' | 'settling'

export interface DragNavRenderState {
  phase: Phase
  direction: DragDirection | null
  /** Immediate previous/next tab currently revealed, if any (null at the
   * ends of tabOrder, or once idle). */
  neighbourTab: Tab | null
  /** True only while settling toward the neighbour becoming the new active
   * tab — this is the signal to mount its real component instead of the
   * lightweight placeholder. */
  committing: boolean
  currentTransform: string
  neighbourTransform: string
  /** Whether a CSS transition should ease the transform (release only) —
   * false during the drag itself, which tracks 1:1 with no easing. */
  animate: boolean
}

// `none`, not `translateX(0)`: a resting identity transform still makes the page slot a containing
// block for `position: fixed` descendants, which trapped every modal and sheet inside the slot's
// box — they rendered inset by the app's padding and stopped short of the bottom nav instead of
// covering the viewport. SwipeBackPage learned the same lesson;
// see the note at the top of SwipeBackPage.css. Only the idle resting value can drop the
// transform — the settling values below must stay transforms so the release still animates.
export const IDLE_STATE: DragNavRenderState = {
  phase: 'idle',
  direction: null,
  neighbourTab: null,
  committing: false,
  currentTransform: 'none',
  neighbourTransform: 'none',
  animate: false,
}

/** Build render state from raw gesture numbers — split out from the touch
 * handlers below so the transform/placeholder logic is easy to reason about
 * (and to unit test) independent of DOM events. */
function buildDraggingState(
  dx: number,
  direction: DragDirection,
  neighbourTab: Tab | null
): DragNavRenderState {
  if (!neighbourTab) {
    // No neighbour at this end of tabOrder: no-op, matching the previous
    // swipe behaviour of simply not changing tabs at the boundary.
    return IDLE_STATE
  }
  const edge = direction === 'left' ? '100%' : '-100%'
  return {
    phase: 'dragging',
    direction,
    neighbourTab,
    committing: false,
    currentTransform: `translateX(${dx}px)`,
    neighbourTransform: `translateX(calc(${edge} + ${dx}px))`,
    animate: false,
  }
}

function buildSettlingState(
  direction: DragDirection,
  neighbourTab: Tab,
  committing: boolean
): DragNavRenderState {
  const offscreen = direction === 'left' ? '-100%' : '100%'
  return {
    phase: 'settling',
    direction,
    neighbourTab,
    committing,
    currentTransform: committing ? `translateX(${offscreen})` : 'translateX(0)',
    neighbourTransform: committing ? 'translateX(0)' : `translateX(${offscreen})`,
    animate: true,
  }
}

interface UseDragNavOptions {
  activeTab: Tab
  tabOrder: Tab[]
  onCommit: (tab: Tab) => void
  /** CSS selector for elements with their own horizontal scrolling (e.g. a
   * chip carousel) — drags starting inside a matching element are ignored
   * so they don't fight with that element's native scroll. */
  excludeSelector?: string
}

/** Real-time, 1:1 finger-tracked drag between adjacent nav tabs. Attaches
 * native touch listeners (not React's synthetic ones) via `containerRef`,
 * since calling preventDefault from a React onTouchMove handler is
 * unreliable — see https://github.com/facebook/react/issues/9809. */
export function useDragNav({
  activeTab,
  tabOrder,
  onCommit,
  excludeSelector,
}: UseDragNavOptions) {
  // A stateful callback ref (rather than a plain useRef) so the touch-listener
  // effect below reliably re-attaches after React's dev StrictMode
  // mount/unmount/remount replay, which nulls and re-fires ref callbacks
  // independently of effect timing.
  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const containerRef = useCallback((el: HTMLDivElement | null) => {
    setNode(el)
  }, [])
  const [state, setState] = useState<DragNavRenderState>(IDLE_STATE)

  const activeTabRef = useRef(activeTab)
  const tabOrderRef = useRef(tabOrder)
  const onCommitRef = useRef(onCommit)
  useEffect(() => {
    activeTabRef.current = activeTab
    tabOrderRef.current = tabOrder
    onCommitRef.current = onCommit
  })

  const rafRef = useRef<number | null>(null)
  const settleRef = useRef<{ committing: boolean; tab: Tab | null }>({
    committing: false,
    tab: null,
  })
  const gestureRef = useRef<{
    active: boolean
    startX: number
    startY: number
    startTime: number
    axis: 'horizontal' | 'vertical' | null
    dx: number
  } | null>(null)

  const neighbourFor = useCallback((direction: DragDirection): Tab | null => {
    const order = tabOrderRef.current
    const currentIndex = order.indexOf(activeTabRef.current)
    const nextIndex = direction === 'left' ? currentIndex + 1 : currentIndex - 1
    if (nextIndex < 0 || nextIndex >= order.length) return null
    return order[nextIndex]
  }, [])

  const handleSettled = useCallback(() => {
    const settle = settleRef.current
    if (settle.committing && settle.tab) {
      onCommitRef.current(settle.tab)
    }
    setState(IDLE_STATE)
  }, [])

  useEffect(() => {
    const el = node
    if (!el) return

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        gestureRef.current = null
        return
      }
      if (excludeSelector) {
        const target = e.target as HTMLElement
        if (target.closest(excludeSelector)) {
          gestureRef.current = null
          return
        }
      }
      const touch = e.touches[0]
      gestureRef.current = {
        active: true,
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        axis: null,
        dx: 0,
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      const gesture = gestureRef.current
      if (!gesture || !gesture.active) return
      const touch = e.touches[0]
      if (!touch) return
      const dx = touch.clientX - gesture.startX
      const dy = touch.clientY - gesture.startY

      if (gesture.axis === null) {
        gesture.axis = resolveDragAxis(dx, dy)
        if (gesture.axis === 'vertical') {
          gesture.active = false
          return
        }
        if (gesture.axis === null) return
      }

      // Horizontal intent locked: own the gesture for its remainder.
      e.preventDefault()
      gesture.dx = dx
      const direction: DragDirection = dx < 0 ? 'left' : 'right'

      if (rafRef.current !== null) return
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        setState(buildDraggingState(gesture.dx, direction, neighbourFor(direction)))
      })
    }

    const onTouchEnd = () => {
      const gesture = gestureRef.current
      gestureRef.current = null
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      if (!gesture || !gesture.active || gesture.axis !== 'horizontal') return

      const durationMs = Date.now() - gesture.startTime
      const dx = gesture.dx
      const direction: DragDirection = dx < 0 ? 'left' : 'right'
      const neighbourTab = neighbourFor(direction)

      if (!neighbourTab) {
        setState(IDLE_STATE)
        return
      }

      const commit = resolveDragCommit(dx, durationMs)
      settleRef.current = { committing: commit, tab: neighbourTab }
      setState(buildSettlingState(direction, neighbourTab, commit))
    }

    const onTouchCancel = () => {
      gestureRef.current = null
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      setState(IDLE_STATE)
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: true })
    el.addEventListener('touchcancel', onTouchCancel, { passive: true })

    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchCancel)
    }
  }, [node, excludeSelector, neighbourFor])

  return { containerRef, drag: state, onSettled: handleSettled }
}
